import { canHaveCustomProperties } from './canHaveCustomProperties'

/** The response body of a C1 API error */
export interface C1ApiError {
  /** Error message. Some endpoints use this to hold stringified JSON when a structured error response is needed. */
  errorMessage: string,
  /** The unique identifier of the request which threw an error. This ID will show up in the API error logs; often with an error message to help with debugging or a stack trace. */
  requestId: string,
}

/**
 * A custom type guard for {@link C1ApiError} (see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards)
 * The primary intention of this function is to test whether a caught error was a well-formed {@link C1ApiError} object
 * @param thing The thing which is being type-guarded
 * @returns Whether the thing is a {@link C1ApiError}
 * @example
 * try {
 *   // this may throw a JS error if there was any kind of runtime error caught within this `try` block; e.g. a bug in axios or if you added any other exception-causing code in this block
 *   // this may throw an axios error without a response; e.g. if the server is down
 *   // this may throw an axios error with a C1ApiError response; e.g. if you don't have permission to access this endpoint
 *   // this may not throw any error; e.g. if the server returns a 200 for any reason
 *   await axios.get('/username')
 * } catch (e: unknown) {
 *   if (!isAxiosError(e)) throw e // this would catch any error that isn't what we expect; because we don't have a way to handle those, the error is re-thrown; you could also handle this more gracefully and tell the user that something went wrong.
 *   if (!isC1ApiError(e.response?.data)) throw new Error(e.response?.statusText) // at this point `e` is an AxiosError, but it catches any errors that don't have the expected C1ApiError response body.
 *   // at this point it is confirmed that `e.data` is a C1ApiError and can be used accordingly without complaint from TypeScript
 *   alert('Error Message: ' + e.data.errorMessage + '\nRequest ID: ' + e.data.requestId)
 * }
 */
export function isC1ApiError(thing: unknown): thing is C1ApiError {
  return canHaveCustomProperties(thing)
    && 'errorMessage' in thing
    && typeof thing.errorMessage === 'string'
    && 'requestId' in thing
    && typeof thing.requestId === 'string'
}
